import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";
import { responsive } from "@/lib/media";
import { BeforeAfter } from "@/components/BeforeAfter";
import { LightboxTopBar, LightboxNavButton, LightboxCaption } from "@/components/lightbox/LightboxParts";
import { sharePhoto } from "@/components/lightbox/sharePhoto";

export { sharePhoto };

const SWIPE_PX = 50;

/** Keyboard (Esc / arrows), body scroll lock and neighbour preloading while the lightbox is open. */
function useLightboxBehaviour({ open, items, index, count, onClose, step }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") step(1);
      else if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, onClose, step]);

  useEffect(() => {
    if (!open || count < 2) return;
    [items[(index + 1) % count], items[(index - 1 + count) % count]].forEach((p) => { const im = new Image(); im.src = p.src; });
  }, [open, index, items, count]);
}

function LightboxImage({ photo: p, loaded, onLoaded }) {
  const fade = `transition-opacity duration-200 ${loaded ? "opacity-100" : "opacity-0"}`;
  if (p.before) {
    return (
      <BeforeAfter key={p.src} before={p.before} after={p.src} alt={p.title} fit="contain" onLoad={onLoaded}
        className={`max-h-full max-w-full rounded-lg overflow-hidden shadow-2xl dg-lightbox-img ${fade}`} imgClassName="max-w-full max-h-[calc(100vh-190px)]" />
    );
  }
  return (
    <img key={p.src} {...responsive(p.src, "100vw")} alt={p.title} decoding="async" onLoad={onLoaded} onClick={(e) => e.stopPropagation()}
      className={`max-h-full max-w-full object-contain rounded-lg shadow-2xl select-none dg-lightbox-img ${fade}`} data-testid="lightbox-image" />
  );
}

export function Lightbox({ items, index, onClose, onChange }) {
  const open = index !== null && index >= 0 && index < items.length;
  const touchX = useRef(null);
  const count = items.length;
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setLoaded(false); }, [index]);

  const step = useCallback((d) => {
    if (count < 2) return;
    onChange((index + d + count) % count);
  }, [count, index, onChange]);
  useLightboxBehaviour({ open, items, index, count, onClose, step });

  if (!open) return null;
  const p = items[index];

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > SWIPE_PX) step(dx < 0 ? 1 : -1);
  };

  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={p.title} className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col dg-lightbox"
      onClick={onClose} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid="lightbox-root">
      <LightboxTopBar index={index} count={count} photo={p} onClose={onClose} />
      <div className="relative flex-1 min-h-0 flex items-center justify-center px-2 sm:px-16">
        {count > 1 && <LightboxNavButton dir={-1} onStep={step} />}
        {!loaded && <Loader2 className="absolute w-8 h-8 text-cyan-400 animate-spin" data-testid="lightbox-loading" />}
        <LightboxImage photo={p} loaded={loaded} onLoaded={() => setLoaded(true)} />
        {count > 1 && <LightboxNavButton dir={1} onStep={step} />}
      </div>
      <LightboxCaption photo={p} />
    </div>,
    document.body
  );
}
